import { useMemo } from 'react';
import { create } from 'zustand';

/**
 * Favorites UI state — which objects render with a filled star.
 *
 * v2 favorites design (two-call split): the server no longer ships a per-row `isFavorite`
 * field. Instead each list hook runs a dedicated "favorites" query (favoritesOnly:true) and
 * reconciles the returned ids into this store, scoped by a key like `screens:item` /
 * `screens:group`. A row's star is filled iff its id is in some scope's set (or has a pending
 * optimistic override). See claude-dev/2026-06-08-favorites/01-spec-large.md.
 *
 * Memory-only (NOT persisted): the server is the source of truth, so on reload the store starts
 * empty and is reseeded by `reconcile` from the favorites-section query. (We deliberately do NOT
 * persist this store — an earlier localStorage attempt fought Apollo's persisted cache: a persisted
 * optimistic override survived reload, `cache-and-network` painted the stale favorites, and
 * `reconcile` clobbered that override.)
 *
 * The favorites-section queries run `cache-and-network` (see the list hooks) so on reload the stars
 * paint from the persisted Apollo cache at the SAME time as the rest of the list, then reconcile
 * against the fresh network response. This no longer risks the clobber above: the store is
 * memory-only (no persisted override to clobber) and the post-toggle `refetch()` always hits the
 * network regardless of fetch policy, so only the initial reload paint reads cache. The one
 * cost is a rare, self-correcting flash — unfavorite an item then reload before the query refetches
 * and its star shows for ~1s before the network response drops it. Worth it to kill the reload lag.
 *
 * Scoping matters: only one feature page is mounted at a time, but items + folders are two
 * separate favorites queries, so each reconciles its own scope key without clobbering the other.
 */
interface FavoritesState {
  /** Authoritative favorite ids per scope key (e.g. 'screens:item', 'assets:group'). */
  scopes: Map<string, Set<string>>;
  /** Optimistic overrides: objectId -> desired favorited state, until the next reconcile. */
  pending: Map<string, boolean>;
  /** Replace a scope's favorite ids from a server favorites-query result; settles overrides. */
  reconcile: (scopeKey: string, favoriteIds: string[]) => void;
  /** Optimistically set an object's favorite state — drives the instant star flip on click. */
  setOptimistic: (objectId: string, favorited: boolean) => void;
  /** Drop an optimistic override (toggle failure → revert to server truth). */
  clearOptimistic: (objectId: string) => void;
  /** Wipe all favorite state. Called on logout + team switch (favorites are per-(user, team)),
   *  so one user's/team's stars never bleed into another's before the next query reconciles. */
  reset: () => void;
}

export const useFavoritesStore = create<FavoritesState>((set) => ({
  scopes: new Map(),
  pending: new Map(),
  reconcile: (scopeKey, favoriteIds) =>
    set((state) => {
      const scopes = new Map(state.scopes);
      const prev = scopes.get(scopeKey) ?? new Set<string>();
      const next = new Set(favoriteIds);
      scopes.set(scopeKey, next);
      // Settle optimistic overrides for any id that was in this scope before (unfavorited)
      // or is in it now (favorited) — the server has now confirmed the truth for them.
      const pending = new Map(state.pending);
      for (const id of prev) pending.delete(id);
      for (const id of next) pending.delete(id);
      return { scopes, pending };
    }),
  setOptimistic: (objectId, favorited) =>
    set((state) => {
      const pending = new Map(state.pending);
      pending.set(objectId, favorited);
      return { pending };
    }),
  clearOptimistic: (objectId) =>
    set((state) => {
      if (!state.pending.has(objectId)) return state;
      const pending = new Map(state.pending);
      pending.delete(objectId);
      return { pending };
    }),
  reset: () => set({ scopes: new Map(), pending: new Map() }),
}));

/**
 * Non-hook read of an object's current favorite state (optimistic override wins, then membership
 * in any scope set). Use inside event handlers / `useFavoritesStore.getState()` selectors and the
 * bulk-action bars where a hook can't be called per-id. `useIsFavorite` is the reactive equivalent.
 */
export function isObjectFavorited(state: FavoritesState, objectId: string): boolean {
  const override = state.pending.get(objectId);
  if (override !== undefined) return override;
  for (const ids of state.scopes.values()) if (ids.has(objectId)) return true;
  return false;
}

/**
 * Reactively filter a list down to items that are STILL favorited — for the Favorites views.
 * When you unfavorite anywhere (optimistic override → false), the item drops out of the list
 * immediately instead of lingering until the next refetch; undo re-favorites → it reappears.
 * Subscribes to the store (pending + scopes) so a toggle anywhere re-renders and re-filters.
 * `active` gates it so it's a no-op outside the favorites view.
 */
export function useFilterFavorited<T extends { _id: string }>(items: T[], active: boolean): T[] {
  const pending = useFavoritesStore((s) => s.pending);
  const scopes = useFavoritesStore((s) => s.scopes);
  return useMemo(() => {
    if (!active) return items;
    return items.filter((it) => {
      const override = pending.get(it._id);
      if (override !== undefined) return override;
      for (const ids of scopes.values()) if (ids.has(it._id)) return true;
      return false;
    });
  }, [items, active, pending, scopes]);
}

/**
 * Reactive selector — is this object currently shown as favorited? Optimistic override wins,
 * then membership in any scope set. Returns a boolean so subscribers only re-render on a flip.
 */
export function useIsFavorite(objectId: string): boolean {
  return useFavoritesStore((s) => isObjectFavorited(s, objectId));
}

/** Subscribe a list hook to the optimistic-override map (re-renders on any star toggle). */
export function usePendingFavorites(): Map<string, boolean> {
  return useFavoritesStore((s) => s.pending);
}

/**
 * Split a list into [favorites-first] sections WITHOUT refetching, so a toggle moves a row
 * between sections instantly and never collapses the paginated list (the cause of the scroll
 * jump). `favItems` = the favoritesOnly query result; `restItems` = the browse "rest" query
 * result (favorites excluded via _id_nin). `pending` overrides win so an optimistic star moves a
 * rest row up (and an optimistic un-star drops a favorite) before the server is re-queried:
 *   - favorites = favItems still favorited (not optimistically removed) ++ rest rows optimistically starred
 *   - rest      = favItems optimistically UN-starred (they'd otherwise vanish — they aren't in the
 *                 _id_nin-excluded rest result) ++ rest rows not (optimistically) favorited
 * Server totals stay put because nothing refetches on toggle, so counts don't flicker either.
 * On the next navigation/focus the queries re-run and everything settles into its sorted slot.
 */
export function partitionFavorites<T>(
  favItems: T[],
  restItems: T[],
  getId: (t: T) => string,
  pending: Map<string, boolean>,
): { favorites: T[]; rest: T[] } {
  const favorites = [
    ...favItems.filter((i) => pending.get(getId(i)) !== false),
    ...restItems.filter((i) => pending.get(getId(i)) === true),
  ];
  const favIdSet = new Set(favItems.map(getId));
  const rest = [
    // A favorite the user just un-starred isn't in the _id_nin-excluded rest result yet, so surface
    // it here (with its data from favItems) instead of letting it disappear until refresh.
    ...favItems.filter((i) => pending.get(getId(i)) === false),
    ...restItems.filter((i) => pending.get(getId(i)) !== true && !favIdSet.has(getId(i))),
  ];
  return { favorites, rest };
}
